import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  LabelList,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  colors,
  spacing,
} from "../../../../../../../../../assets/styles/tokens";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DataItem {
  name: string;
  shortName: string;
  value: number;
  color: string;
  activeColor: string;
}

interface CustomLabelProps {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
  index?: number;
  activeIndex: number | null;
}

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  index?: number;
  activeIndex: number | null;
}

interface LegendDotProps {
  color: string;
  activeColor: string;
  filled: boolean;
  active: boolean;
  anyActive: boolean;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const BASE_DATA: DataItem[] = [
  {
    name: "Sun Bingo",
    shortName: "Sun Bingo",
    value: 105,
    color: "#EA6A00",
    activeColor: "#EA6A00",
  },
  {
    name: "Boyle\nSports C...",
    shortName: "Boyle Sports Casino in Bingo",
    value: 23,
    color: "#3B9EF5",
    activeColor: "#3B9EF5",
  },
  {
    name: "Lottoland\nSport UK",
    shortName: "Lottoland Sport UK",
    value: 67,
    color: "#8B6FF5",
    activeColor: "#8B6FF5",
  },
  {
    name: "Quinn Bet\nSport UK",
    shortName: "Quinn Bet Sport UK",
    value: 105,
    color: "#F87171",
    activeColor: "#F87171",
  },
  {
    name: "Betfred\nSport",
    shortName: "Betfred Sport",
    value: 117,
    color: "#38BDF8",
    activeColor: "#38BDF8",
  },
];

const DIMMED_OPACITY = 0.4;

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Top-of-bar value label — only visible when the bar is selected */
function CustomLabel({
  x = 0,
  y = 0,
  width = 0,
  value = 0,
  index = 0,
  activeIndex,
}: CustomLabelProps) {
  if (activeIndex !== index) return null;
  const fillColor = BASE_DATA[index]?.activeColor ?? BASE_DATA[index]?.color;
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      textAnchor="middle"
      fill={fillColor}
      fontWeight={700}
      fontSize={13}
    >
      {value}%
    </text>
  );
}

/** Multi-line X-axis tick with active/dimmed state */
function CustomXAxisTick({
  x = 0,
  y = 0,
  payload,
  index = 0,
  activeIndex,
}: CustomXAxisTickProps) {
  const lines = (payload?.value ?? "").split("\n");
  const isActive = activeIndex === null || activeIndex === index;
  const baseColor0 = isActive ? "colors.GrayLightMode[700].$value" : "#9CA3AF";
  const baseColor1 = isActive ? "colors.GrayLightMode[700].$value" : "#9CA3AF";
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((line, i) => (
        <text
          key={i}
          x={0}
          y={0}
          dy={16 + i * 14}
          textAnchor="middle"
          fill={i === 0 ? baseColor0 : baseColor1}
          fontSize={10}
          fontWeight={400}
        >
          {line}
        </text>
      ))}
    </g>
  );
}

/** Circular legend indicator — filled for first item, outlined for the rest */
function LegendDot({ color, activeColor, filled, active }: LegendDotProps) {
  // When something is selected: selected bar → activeColor, others → base color (no dim)
  const dotColor = active ? activeColor : color;

  if (filled) {
    return (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: dotColor,
          display: "inline-block",
          transition: "all 0.2s",
        }}
      />
    );
  }
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        display: "inline-block",
        background: dotColor,
        transition: "all 0.2s",
      }}
    />
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BudgetControlGraphView() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handleBarClick = (_: unknown, index: number): void => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const handleLegendClick = (index: number): void => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const anyActive = activeIndex !== null;

  return (
    <div style={{ maxWidth: 560, width: "100%", userSelect: "none" }}>
      {/* ── Legend ── */}
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {BASE_DATA.map((item, i) => {
          const isActive = activeIndex === i;
          // No opacity dimming — unselected items stay full opacity but use base color
          return (
            <div
              key={item.shortName}
              onClick={() => handleLegendClick(i)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                padding: "4px 6px",
                borderRadius: spacing.Radius.xs.$value,
                background: isActive ? `${item.activeColor}` : "transparent",
                transition: "all 0.2s",
              }}
            >
              <LegendDot
                color={item.color}
                activeColor={colors.Base.White.$value}
                filled={i === 0}
                active={isActive}
                anyActive={anyActive}
              />
              <span
                style={{
                  fontSize: 10,
                  color: isActive
                    ? colors.Base.White.$value
                    : colors.GrayLightMode[700].$value,
                  fontWeight: 400,
                  transition: "all 0.2s",
                }}
              >
                {item.shortName}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Chart ── */}
      <ResponsiveContainer width="100%" height={220}>
        <BarChart
          data={BASE_DATA}
          margin={{ top: 20, right: 0, left: -20, bottom: 10 }}
          barCategoryGap="20%"
        >
          <CartesianGrid vertical={false} stroke="#F3F4F6" />

          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={(props) => (
              <CustomXAxisTick
                {...props}
                index={props.index as number}
                activeIndex={activeIndex}
              />
            )}
          />

          <YAxis
            tickFormatter={(v: number) => `${v}%`}
            axisLine={false}
            tickLine={false}
            tick={{ fill: colors.GrayLightMode[700].$value, fontSize: 10 }}
            domain={[0, 120]}
            ticks={[0, 20, 40, 60, 80, 100, 120]}
          />

          <ReferenceLine
            y={100}
            stroke="#9CA3AF"
            strokeDasharray="5 4"
            strokeWidth={1}
          />

          <Bar
            dataKey="value"
            radius={[4, 4, 0, 0]}
            maxBarSize={72}
            onClick={handleBarClick}
            cursor="pointer"
          >
            {BASE_DATA.map((entry, index) => {
              const isActive = activeIndex === index;
              // Selected → activeColor at full opacity
              // Unselected (when something else is selected) → base color dimmed
              // Nothing selected → base color full opacity
              return (
                <Cell
                  key={index}
                  fill={isActive ? entry.activeColor : entry.color}
                  opacity={anyActive && !isActive ? DIMMED_OPACITY : 1}
                  style={{ transition: "opacity 0.2s, fill 0.2s" }}
                />
              );
            })}
            <LabelList
              dataKey="value"
              content={(props) => (
                <CustomLabel
                  {...(props as CustomLabelProps)}
                  activeIndex={activeIndex}
                />
              )}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
